import NProgress from 'nprogress';
import type { Id as ToastNotificationId } from 'react-toastify';
import { toast } from 'react-toastify';
import { findLast, findLastIndex } from 'lodash';
import type { Maybe, Scalars, VolumeUnits, WeightUnits } from '../../graphql';
import type { Vessel } from './Vessel';
import type { EventOrLeg, Events, TimeData } from './TimeData';
import { isDischargeEvent, isCargoEvent } from './TimeData';
import { capacityUnits } from '../../constants/capacityUnits';
import type { Range } from '../../lib/util';
import {
  findIndexWith,
  getPermutations,
  mappedMax,
  mappedMin,
  rangesOverlap,
} from '../../lib/util';
import type { ClientId } from './ClientId';
import type { ScenarioOrder } from '../../lib/planningData';
import type { PhysicalQuantity } from '../../constants/cargoUnits';

export const numToDateString = (d: number): Scalars['Datetime']['input'] =>
  new Date(d).toISOString();

export const dateStringToNum = (d: Scalars['Datetime']['output']): number =>
  new Date(d).getTime();

export const numToDateStringOpt = (
  d?: number | null,
): Maybe<Scalars['Datetime']['input']> =>
  d ? new Date(d).toISOString() : null;

export const numToDateStringOrUndefined = (
  d?: number | null,
): Scalars['Datetime']['input'] | undefined =>
  d ? new Date(d).toISOString() : undefined;

export const dateStringToNumOpt = (
  d?: Maybe<Scalars['Datetime']['output']>,
): number | null => (d ? new Date(d).getTime() : null);

export const stringToNumOpt = (
  x?: string | number | null | undefined,
): number | null | undefined =>
  x === 'NaN' // 'NaN' is a valid value for postgres numeric data type
    ? undefined
    : typeof x === 'string' || typeof x === 'number'
      ? +x
      : x;

export const numToStringOpt = (
  x?: string | number | null | undefined,
): string | null | undefined =>
  typeof x === 'string' || typeof x === 'number' ? x.toString() : x;

export const getUnitSymbol = (unit: VolumeUnits | WeightUnits): string => {
  return capacityUnits[unit].symbol;
};

export const getPreferredCapacityQuantity = (
  vessel: Vessel,
  quantity: PhysicalQuantity = 'volume',
): {
  capacity: number | undefined;
  unit: string | undefined;
} => {
  if (quantity === 'volume' && vessel.volumeCapacity) {
    return {
      capacity: +vessel.volumeCapacity,
      unit: vessel.volumeUnit ? getUnitSymbol(vessel.volumeUnit) : undefined,
    };
  } else if (vessel.massCapacity) {
    return {
      capacity: +vessel.massCapacity,
      unit: vessel.massUnit ? getUnitSymbol(vessel.massUnit) : undefined,
    };
  } else {
    return { capacity: undefined, unit: undefined };
  }
};

const getShipmentIdsInTds = (
  tds: Pick<EventOrLeg, 'event'>[],
): NonNullable<ScenarioOrder['shipmentId']>[] =>
  tds.flatMap(({ event }) =>
    'order' in event && event.order.shipmentId !== null
      ? [event.order.shipmentId]
      : [],
  );

/**
 * Returns a continuous range of all events in all voyages which are overlapped by our given target tds
 */
export const getOverlappingVoyageEvents = (
  tds: TimeData[],
  /** Ordered schedule from which we return a continous range */
  schedule: TimeData[],
): TimeData[] => {
  const initialTimeRange: Range = {
    start: mappedMin(tds, (td) => td.start),
    end: mappedMax(tds, (td) => td.end),
  };

  const scheduleInTimeRange = schedule.filter((td) =>
    rangesOverlap<number>(td, initialTimeRange),
  );
  const shipmentsInTimeRange = new Set<ScenarioOrder['shipmentId']>(
    getShipmentIdsInTds(scheduleInTimeRange),
  );

  const firstEventInAnyShipment = schedule.find(
    ({ event }) =>
      'order' in event && shipmentsInTimeRange.has(event.order.shipmentId),
  );
  const lastEventInAnyShipment = findLast(
    schedule,
    ({ event }) =>
      'order' in event && shipmentsInTimeRange.has(event.order.shipmentId),
  );

  const fullTimeRange: Range = {
    start: Math.min(
      initialTimeRange.start,
      firstEventInAnyShipment?.start ?? Infinity,
    ),
    end: Math.max(
      initialTimeRange.end,
      lastEventInAnyShipment?.end ?? -Infinity,
    ),
  };

  /** All port calls in time range or having any of the touched shipments  */
  const portCallsToInclude: Set<ClientId> = new Set(
    schedule.flatMap((td) =>
      td.event.portCallId !== null &&
      (rangesOverlap(td, fullTimeRange) ||
        ('order' in td.event &&
          shipmentsInTimeRange.has(td.event.order.shipmentId)))
        ? [td.event.portCallId]
        : [],
    ),
  );

  return schedule.filter(
    (td) =>
      (td.event.portCallId !== null &&
        portCallsToInclude.has(td.event.portCallId)) ||
      rangesOverlap<number>(td, fullTimeRange),
  );
};

/**
 * Returns events which take place overlapping the given schedule. If includeByPortCall,
 * events taking place in the same port call regardless of time are also included.
 */
export const getOverlappingEvents = <
  U extends Pick<EventOrLeg, 'start' | 'end' | 'event'>,
>(
  schedule: Pick<EventOrLeg, 'start' | 'end' | 'event'>[],
  events: U[],
  includeByPortCall?: boolean,
): U[] => {
  const shipmentTimeRange: Range = {
    start: schedule[0].start,
    end: schedule[schedule.length - 1].end,
  };

  if (includeByPortCall) {
    const targetPortCallIds: Set<ClientId> = new Set(
      schedule.flatMap((td) =>
        td.event.portCallId !== null ? [td.event.portCallId] : [],
      ),
    );

    return events.filter(
      (td) =>
        (td.event.portCallId !== null &&
          targetPortCallIds.has(td.event.portCallId)) ||
        rangesOverlap<number>(td, shipmentTimeRange),
    );
  } else {
    return events.filter((td) => rangesOverlap<number>(td, shipmentTimeRange));
  }
};

/** Get the range for all events in the range of a voyage, including all the events in the first loading port and last discharging port.
 * If there are any events belonging to another voyage interleaved with this voyage in the array, those events in the range are also included.
 * First index is inclusive, last index is exclusive. */
export function getVoyageEventsRange<T extends Events>(
  events: T[],
  shipmentId: number | ClientId,
): [number, number] | null {
  const firstCargoIndex = events.findIndex(
    (e) => isCargoEvent(e) && e.order.shipmentId === shipmentId,
  );
  const lastCargoIndex = findLastIndex(
    events,
    (e) => isCargoEvent(e) && e.order.shipmentId === shipmentId,
  );

  if (firstCargoIndex === -1 || lastCargoIndex === -1) {
    return null;
  }

  const firstIndex = findIndexWith(
    events,
    0,
    1,
    (e) => e.portCallId === events[firstCargoIndex].portCallId,
  );
  const lastIndex = findIndexWith(
    events,
    events.length - 1,
    -1,
    (e) => e.portCallId === events[lastCargoIndex].portCallId,
  );

  if (firstIndex === undefined || lastIndex === undefined) {
    return null;
  }
  return [firstIndex, lastIndex + 1];
}

export function getVoyageEvents<T extends Events>(
  events: T[],
  shipmentId: number | ClientId,
): T[] {
  const range = getVoyageEventsRange(events, shipmentId);
  if (range) {
    return events.slice(range[0], range[1]);
  } else {
    return [];
  }
}

/** NProgress only has 1 spinner instance. Multiple simultaneous spinners would break it, so only start/stop it with the first/last request. */
let spinningCount = 0;
/**
 * Display a spinner until the given function resolves. If *toastMsg* is given, this is also displayed for the duration of the spinner.
 */
export const withSpinner = <T>(
  fn: () => void | Promise<T>,
  toastMsg?: string,
  toastId?: ToastNotificationId,
): Promise<void | T> => {
  startSpinner();

  return new Promise((resolve, reject) => {
    setTimeout(async () => {
      try {
        const result = await fn();
        resolve(result);
      } catch (err) {
        reject(err);
      }
      endSpinner();
      if (toastId !== undefined) {
        toast.update(toastId, {
          render: toastMsg,
          type: toast.TYPE.INFO,
          isLoading: true,
        });
      }
    });
  });
};

/** Starts a new spinner if one isn't already active, Counts active number of spinner requests. */
export const startSpinner = () => {
  if (spinningCount === 0) {
    NProgress.start();
  }
  spinningCount++;
};

/** Ends the current spinner provided there are no other users left for it. Counts active number of spinner requests. */
export const endSpinner = () => {
  spinningCount = Math.max(0, spinningCount - 1);
  if (spinningCount === 0) {
    NProgress.done();
  }
};

/** Return all permutations for events where there are no violations of load-discharge sequence.
 * Notably a sequence is still considered valid when a discharge exists without load or vice versa and
 * fails only when both are present in the wrong sequence.
 */
export function getValidEventPermutations(events: Events[][]): Events[][][] {
  return getPermutations(events, (head) => {
    // Check for valid Load-Discharge order for all events
    const dischargedOrders = new Set<number>();
    for (const evs of head) {
      for (const e of evs) {
        if (isCargoEvent(e)) {
          if (isDischargeEvent(e)) {
            dischargedOrders.add(e.order.id);
          } else if (dischargedOrders.has(e.order.id)) {
            return false;
          }
        }
      }
    }
    return true;
  });
}

export const defaultConstraints = {
  layday: null,
  cancelling: null,
  rta: null,
  deliveredBefore: null,
};
